import React, { useState } from 'react';
import tinycolor from 'tinycolor2';

import { useModalContext } from '@magoosh/context/modal_context';
import { DefaultFilledButton, SecondaryButton } from '@magoosh/lib/button';
import { CircleIcon, FontAwesomeIcon } from '@magoosh/lib/icons';
import PracticeSessionPreviewModal from '@magoosh/organisms/study_schedule/practice_session_preview_modal/template';
import useAuthenticityToken from '@magoosh/utils/use_authenticity_token';
import { paths } from 'config/path_helpers';
import fetch from 'utilities/fetch';

import { Activity } from '../../types';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

interface Props {
  activity: Activity;
  next?: boolean;
}

export default (props: Props) => {
  const { setModal } = useModalContext();
  const [isStarting, setIsStarting] = useState(false);

  const icon = () => {
    if (props.activity.status === 'finished') {
      return (
        <CircleIcon
          bordered={false}
          colorPalette={{ dark: colors.green, light: colors.greenLight }}
          size={30}
          style={{ WebkitTextStroke: `1px ${colors.greenLight}` }}
        >
          <FontAwesomeIcon icon="check" />
        </CircleIcon>
      );
    } else if (isDrill()) {
      return (
        <CircleIcon
          colorPalette={{
            dark: colors.brandWarning,
            light: tinycolor(colors.brandWarning).lighten(20).toString()
          }}
          size={25}
        >
          <FontAwesomeIcon icon="bolt" />
        </CircleIcon>
      );
    } else if (isPractice()) {
      if (props.activity.content.practiceSessionConfig?.difficulty === 'Easy') {
        return (
          <div style={{ backgroundColor: colors.brandPrimary, borderRadius: 20, height: 14, width: 14 }} />
        );
      } else if (props.activity.content.practiceSessionConfig?.difficulty === 'Medium') {
        return (
          <div style={{ backgroundColor: colors.dataDarkBlue, height: 14, width: 14 }} />
        );
      }  else if (props.activity.content.practiceSessionConfig?.difficulty === 'Hard') {
        return (
          <div className={style.blackDiamond} />
        );
      } else if (props.activity.content.practiceSessionConfig?.difficulty === 'Very Hard') {
        return (
          <div className="d-flex">
            <div className={style.blackDiamond} />
            <div className={style.blackDiamond} style={{ marginLeft: -4 }} />
          </div>
        );
      } else {
        return (
          <CircleIcon
            colorPalette={{
              dark: colors.brandInfo,
              light: tinycolor(colors.brandInfo).lighten(30).toString()
            }}
            size={25}
          >
            <FontAwesomeIcon className="small" icon="pencil" />
          </CircleIcon>
        );
      }
    }
  };

  const isDrill = () => {
    if (props.activity.type !== 'practice') return false;
    return props.activity.content.type === 'drills';
  };

  const isPractice = () => {
    if (props.activity.type !== 'practice') return false;
    return props.activity.content.type === 'practice';
  };

  const presentPracticePreviewModal = (contentBlockId) => {
    setIsStarting(true);

    fetch(paths.api.studySchedulePractice(contentBlockId), {
      method: 'POST'
    }).then((response: any) => {
      if (response.redirect) {
        window.location.assign(response.redirect);
        return;
      }

      setModal({
        body: (
          <PracticeSessionPreviewModal
            onHide={() => {
              setIsStarting(false);
              setModal(null);
            }}
            {...response.preview}
          />
        )
      });
    });
  };

  const renderStartButton = () => {
    if (!props.activity.content.href && (!props.activity.content.questionsAvailable || props.activity.locked)) {
      return (
        <DefaultFilledButton disabled>
          {props.activity.locked && <FontAwesomeIcon icon="lock" />} Start
        </DefaultFilledButton>
      );
    }

    const authenticityToken = useAuthenticityToken();

    const Button = props.next ? SecondaryButton : DefaultFilledButton;
    const text = props.activity.status === 'finished' ? 'Retake' : 'Start';

    if (props.activity.type === 'practice' && props.activity.content.href) {
      return (
        <Button href={props.activity.content.href}>
          {text}
        </Button>
      );
    } else if (props.activity.type === 'practice' && props.activity.content.practiceSessionConfig) {
      const config = props.activity.content.practiceSessionConfig;
      const urlParams = [
        ['practice_session[exam_section_id]', config.examSectionId.toString()],
        ['practice_session[difficulty_range_str]', config.difficultyRangeStr]
      ];

      if (config.tagIds) {
        config.tagIds.forEach((tagId) => (
          urlParams.push(['practice_session[tag_ids][]', tagId.toString()])
        ));
      }

      return (
        <form method="POST" action="/practices">
          <input type="hidden" name="authenticity_token" value={authenticityToken} />
          {
            urlParams.map((paramTuple) => (
              <input key={paramTuple[0]} type="hidden" name={paramTuple[0]} value={paramTuple[1]} />
            ))
          }

          <Button block type="submit">
            {text}
          </Button>
        </form>
      );
    } else if (props.activity.type === 'practice' && props.activity.content.contentBlockId) {
      return (
        <Button
          onClick={() => presentPracticePreviewModal(props.activity.content.contentBlockId)}
          submitting={isStarting}
        >
          {text}
        </Button>
      );
    } else {
      // TODO - log unexpected case
    }
  };

  return (
    <>
      <div className="align-items-center d-flex gap-m">
        <div className="flex-1">
          <div className="align-items-center d-flex gap-s">
            <div>{icon()}</div>
            
            <div style={{ lineHeight: 1.15 }}>
              <strong>{props.activity.name}</strong>

              {
                props.activity.nameDetail && (
                  <div className="small text-gray-light">
                    {props.activity.nameDetail}
                  </div>
                )
              }
            </div>

            {
              props.activity.description && (
                <div className="align-self-start d-flex flex-1 justify-content-end small text-gray-light">
                  {props.activity.description}
                </div>
              )
            }
          </div>
        </div>
      </div>

      {(() => {
        if (props.next) {
          return renderStartButton();
        } else if (props.activity.status === 'finished') {
          const reviewPath = paths.reviewAnswers({
            'filters[practice_session_id]': props.activity.content.practiceSessionId,
            study_item: props.activity.content.contentBlockId
          });

          return (
            <div className="align-items-center d-flex gap-xs">
              <DefaultFilledButton className="flex-1" href={reviewPath}>Review</DefaultFilledButton>
              {renderStartButton()}
            </div>
            );
        } else {
          return renderStartButton();
        }
      })()}

      {
        props.activity.hint && (
          <div className="hidden-xs small" dangerouslySetInnerHTML={{ __html: props.activity.hint }} />
        )
      }
    </>
  );
};
